import React from 'react';
import Container from '../common/Container';
import Button from '../common/Button';
import Header from '../layout/Header';
import DailyAverageScore from '../common/DailyAverageScore';

interface GameStatus {
  played: boolean;
  score: number;
  total: number;
}

interface StartScreenProps {
  onStartText: () => void;
  onStartPicture: () => void;
  isLoading?: boolean;
  gameDate?: Date;
  textGameStatus: GameStatus;
  pictureGameStatus: GameStatus;
  hasSavedTextProgress?: boolean;
  hasSavedPictureProgress?: boolean;
}

const StartScreen: React.FC<StartScreenProps> = ({
  onStartText,
  onStartPicture,
  isLoading = false,
  gameDate,
  textGameStatus,
  pictureGameStatus,
  hasSavedTextProgress = false,
  hasSavedPictureProgress = false,
}) => {
  return (
    <div className="min-h-screen bg-dark-950 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-circuit opacity-5"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>

      <Header />

      <Container maxWidth="lg" className="py-6 sm:py-12 px-4 sm:px-6 relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="relative inline-block mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-secondary-400 to-primary-400 animate-glow tracking-wider">
              AI OR HUMAN?
            </h1>
            <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 blur-xl rounded-lg"></div>
          </div>

          {gameDate && (
            <div className="mb-6 sm:mb-8">
              <div className="inline-flex items-center bg-gradient-to-r from-primary-600 to-primary-700 border border-primary-500 px-4 sm:px-6 py-2 sm:py-3 rounded-lg shadow-neon mb-4">
                <div className="w-2 h-2 bg-accent-400 rounded-full animate-pulse mr-2 sm:mr-3"></div>
                <span className="font-display font-semibold text-white tracking-wider uppercase text-xs sm:text-sm">
                  Daily Challenge • {gameDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Game Mode Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Text Game Card */}
          <div className="card-robotic p-6 flex flex-col h-full relative group hover:border-primary-500 transition-colors duration-300">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <svg className="w-24 h-24 text-primary-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 17H4v2h10v-2zm6-8H4v2h16V9zM4 15h16v-2H4v2zM4 5v2h16V5H4z" />
              </svg>
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center border border-primary-500/30">
                  <span className="text-2xl">📝</span>
                </div>
                <DailyAverageScore mode="text" className="transform scale-90 origin-top-right" />
              </div>
              <h2 className="text-2xl font-display font-bold text-white mb-2">Text Analysis</h2>
              <p className="text-dark-200 text-sm mb-4">
                Identify which text passage was written by AI and which by a human.
              </p>
            </div>

            <div className="mt-auto">
              {textGameStatus.played ? (
                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-dark-300 text-xs uppercase tracking-wider">Status</span>
                    <span className="text-accent-400 text-xs font-bold uppercase">Completed</span>
                  </div>
                  <div className="text-2xl font-display font-bold text-white text-center">
                    {textGameStatus.score}/{textGameStatus.total}
                  </div>
                  <div className="text-center text-dark-400 text-xs mt-1">Correct Answers</div>
                </div>
              ) : (
                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-dark-300 text-xs uppercase tracking-wider">Challenge</span>
                    <span className="text-primary-400 text-xs font-bold uppercase">{textGameStatus.total} Rounds</span>
                  </div>
                </div>
              )}

              <Button
                onClick={onStartText}
                variant={textGameStatus.played ? "secondary" : "primary"}
                fullWidth
                disabled={isLoading}
              >
                {textGameStatus.played ? "VIEW RESULTS" : (hasSavedTextProgress ? "RESUME TEXT CHALLENGE" : "START TEXT CHALLENGE")}
              </Button>
            </div>
          </div>

          {/* Picture Game Card */}
          <div className="card-robotic p-6 flex flex-col h-full relative group hover:border-secondary-500 transition-colors duration-300">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <svg className="w-24 h-24 text-secondary-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
              </svg>
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 bg-secondary-500/20 rounded-lg flex items-center justify-center border border-secondary-500/30">
                  <span className="text-2xl">🖼️</span>
                </div>
                <DailyAverageScore mode="picture" className="transform scale-90 origin-top-right" />
              </div>
              <h2 className="text-2xl font-display font-bold text-white mb-2">Visual Analysis</h2>
              <p className="text-dark-200 text-sm mb-4">
                Distinguish between real photographs and AI-generated imagery.
              </p>
            </div>

            <div className="mt-auto">
              {pictureGameStatus.played ? (
                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-dark-300 text-xs uppercase tracking-wider">Status</span>
                    <span className="text-accent-400 text-xs font-bold uppercase">Completed</span>
                  </div>
                  <div className="text-2xl font-display font-bold text-white text-center">
                    {pictureGameStatus.score}/{pictureGameStatus.total}
                  </div>
                  <div className="text-center text-dark-400 text-xs mt-1">Correct Answers</div>
                </div>
              ) : (
                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-dark-300 text-xs uppercase tracking-wider">Challenge</span>
                    <span className="text-secondary-400 text-xs font-bold uppercase">{pictureGameStatus.total} Rounds</span>
                  </div>
                </div>
              )}

              <Button
                onClick={onStartPicture}
                variant={pictureGameStatus.played ? "secondary" : "secondary"}
                className={!pictureGameStatus.played ? "!bg-gradient-to-r !from-secondary-600 !to-secondary-700 !border-secondary-500" : ""}
                fullWidth
                disabled={isLoading}
              >
                {pictureGameStatus.played ? "VIEW RESULTS" : (hasSavedPictureProgress ? "RESUME VISUAL CHALLENGE" : "START VISUAL CHALLENGE")}
              </Button>
            </div>
          </div>
        </div>

        {/* Instructions Summary */}
        <div className="mt-12 text-center max-w-2xl mx-auto">
          <p className="text-dark-300 text-sm">
            Complete both challenges daily to maintain your streak and contribute to the global human verification database.
          </p>
        </div>
      </Container>
    </div>
  );
};

export default StartScreen;
